import Table from 'components/ui/base/table/Table';
import useDeclarations from 'hooks/useDeclarations';
import { FC, useCallback, useMemo, useReducer } from 'react';
import { getConversationId, getFormHistoryDescription, HistoryUtils } from './historyUtils';
import { HistoryViewContainer, HistoryStatusContainer } from './components/styledComponents';
import { H5 } from 'components/ui/base/typography';
import { orderBy } from 'lodash';
import { DeclarationNotificationsDrawer } from 'views/declarations/common/declaration-errors-drawer/DeclarationNotificationsDrawerButton';
import { useNavigate, useParams } from 'react-router-dom';
import { AesNotification } from 'store/declarations/ireland/export-declaration';
import { getIcs2NotificationDescription, ICS2Notification } from 'store/declarations/eu/ics2/ics2-notification';
import DeclarationTabKeys from 'views/declarations/utils/DeclarationTabKeys';

export type Column = {
    key?: string;
    date: string;
    user: string;
    action: string;
    description: string;
    notificationType?: string;
    errors?: Object;
    conversationId?: string[];
    rawMessage?: string;
    customsRequest?: string;
};
interface ErrorsDrawer {
    drawerOpen: boolean;
    errors: Object | undefined;
    showRawData: boolean;
}

const DeclarationHistoryTable: FC<{}> = () => {
    const { declarationHistory, declaration } = useDeclarations();
    const navigate = useNavigate();
    const { declarationId, manifestId, jobId } = useParams<{
        declarationId?: string;
        manifestId?: string;
        jobId?: string;
    }>();

    const openCustomsRequests = useCallback(() => {
        if (!declarationId) return;
        const base =
            manifestId && jobId
                ? `/manifests/${manifestId}/${jobId}/${declarationId}`
                : `/declarations/${declarationId}`;
        navigate(`${base}/${DeclarationTabKeys.CUSTOMS_REQUESTS}`);
    }, [navigate, declarationId, manifestId, jobId]);

    const [errorsDrawer, setErrorsDrawer] = useReducer(
        (initialState: ErrorsDrawer, newState: Partial<ErrorsDrawer>) => ({
            ...initialState,
            ...newState,
        }),
        { drawerOpen: false, errors: undefined, showRawData: false }
    );

    const getAesDescription = useCallback(
        (notification: AesNotification) => {
            if (!declaration?.ieExportDeclaration) return '';

            if (!notification?.additionalProperties?.messageName) return notification.messageType;

            return `${notification.messageType} - ${notification.additionalProperties.messageName}`;
        },
        [declaration?.ieExportDeclaration]
    );

    const currentKeys = useMemo(() => {
        let keys: Column = {} as Column;

        if (
            declaration?.irelandImportDeclaration ||
            declaration?.irelandH7ImportDeclaration ||
            declaration?.entrySummaryDeclaration
        ) {
            keys = {
                date: 'notificationDate',
                action: 'type',
                description: 'message',
                user: 'user',
                errors: 'errors',
                conversationId: ['transactionId'],
                rawMessage: 'rawMessage',
            };
        } else if (declaration?.cdsDeclaration) {
            keys = {
                date: 'issueDateTime',
                action: 'statusMessage',
                notificationType: 'functionCode',
                description: 'message',
                user: 'user',
                errors: 'errors',
                rawMessage: 'rawMessage',
                conversationId: ['transactionId', 'conversationId'],
            };
        } else if (
            declaration?.ieExportDeclaration ||
            declaration?.gvmsDeclaration ||
            declaration?.ieArrivalAtExitDeclaration ||
            declaration?.ieNctsDeclaration ||
            declaration?.ukNctsDeclaration
        ) {
            keys = {
                date: 'createdAt',
                action: 'type',
                description: 'messageType',
                user: 'user',
                errors: 'errors',
                rawMessage: 'rawMessage',
                conversationId: ['message.messageId', 'transactionId'],
            };
        } else if (declaration?.ics2ENSDeclaration) {
            keys = {
                date: 'createdAt',
                action: 'messageType',
                description: 'description',
                user: 'user',
                errors: 'errors',
                rawMessage: 'rawMessage',
                conversationId: ['transactionId'],
                customsRequest: 'messageType',
            };
        }

        return keys;
    }, [
        declaration?.ieArrivalAtExitDeclaration,
        declaration?.ics2ENSDeclaration,
        declaration?.cdsDeclaration,
        declaration?.entrySummaryDeclaration,
        declaration?.irelandH7ImportDeclaration,
        declaration?.irelandImportDeclaration,
        declaration?.ieExportDeclaration,
        declaration?.gvmsDeclaration,
        declaration?.ieNctsDeclaration,
        declaration?.ukNctsDeclaration,
    ]);

    const history = useMemo(() => {
        const statusHistory =
            declaration?.irelandImportDeclaration?.notifications ??
            declaration?.irelandH7ImportDeclaration?.notifications ??
            declaration?.entrySummaryDeclaration?.notifications ??
            declaration?.cdsDeclaration?.notifications ??
            declaration?.ieExportDeclaration?.notifications ??
            declaration?.gvmsDeclaration?.notifications ??
            declaration?.ieArrivalAtExitDeclaration?.notifications ??
            declaration?.ieNctsDeclaration?.notifications ??
            declaration?.ukNctsDeclaration?.notifications ??
            declaration?.ics2ENSDeclaration?.notifications ??
            [];

        const _formHistory = declarationHistory ?? [];
        let formHistory: any[] = [];
        formHistory = _formHistory.map((h, i) => {
            const conversationId = getConversationId(h, currentKeys.conversationId);
            return {
                user: h.creatorName,
                key: `form-history-${i}`,
                [currentKeys.action]: h.type,
                [currentKeys.date]: h.createdAt,
                [currentKeys.description]: getFormHistoryDescription(h),
                ...(conversationId.key ? ({ [conversationId.key]: conversationId.value } as any) : {}),
            };
        }) as Column[];

        const history = [
            ...statusHistory.map((h, i) => ({
                ...h,
                key: `status-history-${i}`,
                user: 'Customs',
                errors: h,
                ...(declaration?.ieExportDeclaration && { messageType: getAesDescription(h as AesNotification) }),
                ...(declaration?.ics2ENSDeclaration && {
                    description: getIcs2NotificationDescription(h as ICS2Notification),
                }),
            })),
            ...formHistory,
        ];

        return orderBy(history, currentKeys.date, 'desc');
    }, [
        currentKeys.action,
        currentKeys.conversationId,
        currentKeys.date,
        currentKeys.description,
        declaration?.cdsDeclaration?.notifications,
        declaration?.entrySummaryDeclaration?.notifications,
        declaration?.gvmsDeclaration?.notifications,
        declaration?.ieArrivalAtExitDeclaration?.notifications,
        declaration?.ics2ENSDeclaration,
        declaration?.ieExportDeclaration,
        declaration?.ieNctsDeclaration?.notifications,
        declaration?.irelandH7ImportDeclaration?.notifications,
        declaration?.irelandImportDeclaration?.notifications,
        declaration?.ukNctsDeclaration?.notifications,
        declarationHistory,
        getAesDescription,
    ]);

    const openErrorDrawer = ({ errors, showRawData }: { errors: Object; showRawData: boolean }) => {
        setErrorsDrawer({ errors, drawerOpen: true, showRawData });
    };
    const closeErrorDrawer = () => {
        setErrorsDrawer({ errors: undefined, drawerOpen: false });
    };

    const historyColumns = useMemo(
        () => HistoryUtils.columns.statusHistory(currentKeys, openErrorDrawer, declaration, openCustomsRequests),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [currentKeys, openCustomsRequests]
    );

    return (
        <HistoryViewContainer>
            <HistoryStatusContainer>
                <H5 style={{ marginBottom: 24 }}>Status History</H5>
                <Table
                    pagination={false}
                    columns={historyColumns}
                    dataSource={history}
                    onRow={() => {
                        return {
                            'data-testid': `status-history-table-row`,
                            onClick: () => {},
                        };
                    }}
                />
            </HistoryStatusContainer>
            <DeclarationNotificationsDrawer
                visible={errorsDrawer.drawerOpen}
                onClose={closeErrorDrawer}
                declaration={errorsDrawer.errors as any}
                showRawData={errorsDrawer.showRawData}
            />
        </HistoryViewContainer>
    );
};

export default DeclarationHistoryTable;
